import { canMoveToFoundation, canMoveToTableau } from "./rules";
import { foundationIndex } from "./rules";
import type { Card, GameState, MoveSource } from "./types";

/**
 * Solveur d'indices et de blocage — la seule vraie difference structurelle
 * avec les moteurs de battle/farkle (pas d'IA adverse ici, cahier des charges
 * §10) : il cherche un coup UTILE, au sens ou l'entendent les implementations
 * de reference du Klondike, et sert a la fois au bouton Indice et a la
 * detection "plus aucun coup possible".
 *
 * Un coup est juge utile s'il :
 *  - pose une carte sur une fondation ;
 *  - deplace une sequence du tableau en decouvrant une carte cachee
 *    (ou en liberant une colonne pour un Roi qui decouvrirait autre chose) ;
 *  - joue une carte de la defausse sur le tableau ;
 *  - ou si une carte atteignable en piochant (en tenant compte du tirage par
 *    1 ou 3 et du recyclage illimite) est jouable.
 * Les allers-retours steriles (Roi deja sur colonne vide deplace vers une
 * autre colonne vide, carte redescendue d'une fondation...) ne comptent pas.
 */

export interface Hint {
  /** D'ou part le coup suggere ("draw" = piocher). */
  source: MoveSource | { type: "draw" };
  /** Destination (absente pour "draw"). */
  target?: { type: "foundation" } | { type: "tableau"; col: number };
  /** Phrase courte affichable telle quelle dans le HUD. */
  label: string;
}

const RANK_NAMES: Record<number, string> = {
  1: "l'As", 2: "le 2", 3: "le 3", 4: "le 4", 5: "le 5", 6: "le 6", 7: "le 7",
  8: "le 8", 9: "le 9", 10: "le 10", 11: "le Valet", 12: "la Dame", 13: "le Roi",
};

const SUIT_NAMES: Record<Card["suit"], string> = {
  spades: "de pique",
  hearts: "de cœur",
  diamonds: "de carreau",
  clubs: "de trèfle",
};

export function cardName(card: Card): string {
  return `${RANK_NAMES[card.rank]} ${SUIT_NAMES[card.suit]}`;
}

/** Premier coup utile trouve, par ordre de priorite ; null si la partie est bloquee. */
export function findHint(state: GameState): Hint | null {
  // 1. Vers une fondation (defausse ou sommet de colonne).
  const foundationHint = findFoundationMove(state);
  if (foundationHint) return foundationHint;

  // 2. Sequence du tableau qui decouvre une carte cachee.
  for (let col = 0; col < state.tableau.length; col++) {
    const pile = state.tableau[col]!;
    const start = pile.findIndex((card) => card.faceUp);
    if (start < 0) continue;
    const moving = pile[start]!;
    // Roi deja au fond d'une colonne : le deplacer vers une autre colonne
    // vide ne decouvre rien, c'est un aller-retour sterile.
    if (start === 0 && moving.rank === 13) continue;
    for (let to = 0; to < state.tableau.length; to++) {
      if (to === col) continue;
      if (canMoveToTableau(moving, state.tableau[to]!)) {
        return {
          source: { type: "tableau", col, index: start },
          target: { type: "tableau", col: to },
          label: `Déplacez ${cardName(moving)} sur la colonne ${to + 1}`,
        };
      }
    }
  }

  // 3. Defausse vers le tableau.
  const wasteTop = state.waste[state.waste.length - 1];
  if (wasteTop) {
    for (let to = 0; to < state.tableau.length; to++) {
      if (canMoveToTableau(wasteTop, state.tableau[to]!)) {
        return {
          source: { type: "waste" },
          target: { type: "tableau", col: to },
          label: `Jouez ${cardName(wasteTop)} de la défausse sur la colonne ${to + 1}`,
        };
      }
    }
  }

  // 4. Une carte atteignable en piochant est jouable quelque part.
  for (const card of reachableWasteTops(state)) {
    if (isPlayableSomewhere(card, state)) {
      return { source: { type: "draw" }, label: "Piochez : une carte jouable arrive" };
    }
  }

  return null;
}

/** Coup direct vers une fondation, s'il en existe un (aussi utilise par l'auto-complete). */
export function findFoundationMove(state: GameState): Hint | null {
  for (let col = 0; col < state.tableau.length; col++) {
    const pile = state.tableau[col]!;
    const top = pile[pile.length - 1];
    if (top && top.faceUp && canMoveToFoundation(top, state.foundations[foundationIndex(top.suit)]!, top.suit)) {
      return {
        source: { type: "tableau", col, index: pile.length - 1 },
        target: { type: "foundation" },
        label: `Montez ${cardName(top)} sur sa fondation`,
      };
    }
  }
  const wasteTop = state.waste[state.waste.length - 1];
  if (wasteTop && canMoveToFoundation(wasteTop, state.foundations[foundationIndex(wasteTop.suit)]!, wasteTop.suit)) {
    return {
      source: { type: "waste" },
      target: { type: "foundation" },
      label: `Montez ${cardName(wasteTop)} sur sa fondation`,
    };
  }
  return null;
}

/** Partie bloquee : plus aucun coup utile, meme en piochant indefiniment. */
export function isBlocked(state: GameState): boolean {
  return findHint(state) === null;
}

/**
 * Toutes les cartes qui peuvent devenir le sommet de la defausse en piochant,
 * sans jouer aucun coup entre-temps : simulation du cycle pioche/defausse
 * (tirage par drawCount, recyclage illimite). Comme piocher ne change jamais
 * l'ordre des cartes, l'ensemble est fini et la simulation boucle vite.
 */
export function reachableWasteTops(state: GameState): Card[] {
  const tops: Card[] = [];
  const seen = new Set<string>();
  const push = (card: Card | undefined): void => {
    if (!card) return;
    const key = `${card.suit}-${card.rank}`;
    if (!seen.has(key)) {
      seen.add(key);
      tops.push(card);
    }
  };

  let stock = [...state.stock];
  let waste = [...state.waste];
  push(waste[waste.length - 1]);

  // Deux recyclages suffisent a couvrir tous les alignements possibles du
  // tirage par 3 (l'ordre est conserve) ; la borne large protege du reste.
  let recycles = 0;
  for (let guard = 0; guard < 200; guard++) {
    if (stock.length === 0) {
      if (waste.length === 0 || recycles >= 2) break;
      stock = [...waste].reverse();
      waste = [];
      recycles++;
      continue;
    }
    const count = Math.min(state.drawCount, stock.length);
    for (let i = 0; i < count; i++) waste.push(stock.pop()!);
    push(waste[waste.length - 1]);
  }
  return tops;
}

function isPlayableSomewhere(card: Card, state: GameState): boolean {
  if (canMoveToFoundation(card, state.foundations[foundationIndex(card.suit)]!, card.suit)) return true;
  return state.tableau.some((pile) => canMoveToTableau(card, pile));
}
